import React, { useEffect, useState } from 'react';
import { MapIcon } from '@heroicons/react/24/outline';
import baseUrl from '../../config';

// date-fns utilities
import {
  startOfMonth,
  endOfMonth,
  getDay,
  eachDayOfInterval,
  isSameDay,
  isPast,
  format,
  parseISO, // Changed from parseISO to keep original logic, but be mindful of timezones
} from 'date-fns';

// Material UI components
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Define missing constants
const getAuthToken = () => localStorage.getItem('token');

const AdventureMap = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Invitation dialog states
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [selectedEventForInvite, setSelectedEventForInvite] = useState(null);
  const [inviteeEmail, setInviteeEmail] = useState('');
  const [generatedInviteLink, setGeneratedInviteLink] = useState('');
  const [inviteLinkLoading, setInviteLinkLoading] = useState(false);

  // Snackbar states for Adventure Map
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const fetchUpcomingEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1; // getMonth() is 0-indexed

      const response = await fetch(`${baseUrl}api/crm/adventure-map/upcoming?month=${month}&year=${year}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch upcoming events.');
      }

      const data = await response.json();

      if (data.success) {
        // Parse dates from the API response to Date objects for consistent handling
        const parsedEvents = data.data.map(event => ({
          ...event,
          // Use displayDate for calendar display, which is already in user's timezone
          date: parseISO(event.displayDate),
        }));
        setUpcomingEvents(parsedEvents);
      } else {
        setError(data.message || 'Failed to fetch upcoming events.');
      }
    } catch (err) {
      console.error("Error fetching upcoming events:", err);
      setError(err.message || 'Failed to load upcoming events. Please try again later.');
      setSnackbarMessage(err.message || 'Failed to load upcoming events.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpcomingEvents();
  }, [currentMonth]); // Refetch when month changes

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Calendar logic
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const startDay = startOfMonth(currentMonth);
  const endDay = endOfMonth(currentMonth);

  // Calculate days for the calendar grid
  const firstDayOfWeek = getDay(startDay);
  const days = eachDayOfInterval({ start: startDay, end: endDay });

  // Add leading days from previous month
  for (let i = 0; i < firstDayOfWeek; i++) {
    days.unshift(new Date(startDay.getFullYear(), startDay.getMonth(), startDay.getDate() - (firstDayOfWeek - i)));
  }

  // Add trailing days from next month
  const totalCells = Math.ceil(days.length / 7) * 7;
  for (let i = days.length; i < totalCells; i++) {
    days.push(new Date(endDay.getFullYear(), endDay.getMonth(), endDay.getDate() + (i - days.length + 1)));
  }

  const handleOpenInviteDialog = (event) => {
    setSelectedEventForInvite(event);
    setInviteeEmail('');
    setGeneratedInviteLink(''); // Clear previous link
    setOpenInviteDialog(true);
  };

  const handleCloseInviteDialog = () => {
    setOpenInviteDialog(false);
    setSelectedEventForInvite(null);
    setInviteeEmail('');
    setGeneratedInviteLink('');
  };

  const handleGenerateInviteLink = async () => {
    setInviteLinkLoading(true);
    setGeneratedInviteLink(''); // Clear previous link
    try {
      const response = await fetch(`${baseUrl}api/crm/generate-link/${selectedEventForInvite._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ inviteeEmail }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to generate invitation link. Status: ${response.status}`);
      }

      const result = await response.json();
      setGeneratedInviteLink(result.invitationLink);
      setSnackbarMessage('Invitation link generated successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error generating invitation link:', err);
      setSnackbarMessage(`Failed to generate invitation link: ${err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setInviteLinkLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-glass border-b-4 border-orange-600 transition-all duration-300 hover:shadow-xl hover:scale-[1.01]">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 flex items-center">
        <MapIcon className="w-8 h-8 mr-3 text-orange-600" />
        Adventure Map
      </h2>
      <div className="bg-gray-50 rounded-lg p-4 mb-4 shadow-sm">
        <div className="flex justify-between items-center mb-3">
          <IconButton onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>
            {'<'}
          </IconButton>
          <span className="font-bold text-xl text-gray-800">{format(currentMonth, 'MMMM yyyy')}</span>
          <IconButton onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>
            {'>'}
          </IconButton>
        </div>
        <div className="grid grid-cols-7 text-center text-sm font-semibold text-gray-500 mb-2">
          {daysOfWeek.map(day => <span key={day}>{day}</span>)}
        </div>
        <div className="grid grid-cols-7 text-center text-gray-800">
          {days.map((day, index) => {
            // Filter events based on the `date` property, which is now a Date object from `displayDate`
            const dayEvents = upcomingEvents.filter(event =>
              isSameDay(event.date, day)
            );
            const isToday = isSameDay(day, new Date());
            const isCurrentMonth = day.getMonth() === currentMonth.getMonth();

            return (
              <span
                key={index}
                className={`py-1 relative flex flex-col items-center justify-center rounded-full text-sm
                  ${!isCurrentMonth ? 'text-gray-400' : ''}
                  ${isToday ? 'bg-blue-200 text-blue-800 font-bold' : ''}
                  ${dayEvents.length > 0 && isCurrentMonth && !isToday ? 'bg-indigo-200 text-indigo-800 font-bold' : ''}
                  ${isPast(day) && !isToday && isCurrentMonth ? 'text-gray-500' : ''}
                  min-h-[30px]
                `}
              >
                {format(day, 'd')}
                {dayEvents.length > 0 && isCurrentMonth && (
                  <span className="absolute bottom-0 right-0 left-0 text-center text-[8px] leading-none">
                    <span className="bg-orange-500 text-white rounded-full px-1 py-0.5">
                      {dayEvents.length}
                    </span>
                  </span>
                )}
              </span>
            );
          })}
        </div>
      </div>
      <h3 className="text-xl font-semibold text-gray-900 mb-3">Upcoming Events:</h3>
      <ul className="space-y-3">
        {loading ? (
          <li className="flex justify-center items-center py-4">
            <CircularProgress />
          </li>
        ) : error ? (
          <li className="text-red-600 text-center py-4">{error}</li>
        ) : upcomingEvents.length > 0 ? (
          upcomingEvents.map((event) => (
            <li key={event._id} className="flex flex-col sm:flex-row items-start sm:items-center bg-gray-50 p-3 rounded-lg shadow-sm">
              <div className="flex-grow">
                <span className={`text-sm font-bold ${event.color} mr-2`}>{event.title}</span>
                {/* Use displayDate, displayStartTime, displayEndTime from the API for correct timezone */}
                <span className="text-gray-800">
                  {event.displayDate ? format(parseISO(event.displayDate), 'MMM dd, yyyy') : 'N/A'} at {event.displayStartTime} - {event.displayEndTime} ({event.displayTimezone})
                </span>
              </div>
              <div className="flex gap-2 mt-2 sm:mt-0 sm:ml-auto">
                {event.meetingLink && (
                  <a
                    href={event.meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1 bg-green-500 text-white rounded-md text-sm hover:bg-green-600 transition-colors"
                  >
                    Join
                  </a>
                )}
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleOpenInviteDialog(event)}
                  className="px-3 py-1 bg-orange-500 text-white rounded-md text-sm hover:bg-orange-600 transition-colors"
                >
                  Invite
                </Button>
              </div>
            </li>
          ))
        ) : (
          <li className="text-gray-600">No upcoming events for this month.</li>
        )}
      </ul>

      {/* Invitation Generation Dialog */}
      <Dialog open={openInviteDialog} onClose={handleCloseInviteDialog} fullWidth maxWidth="sm">
        <DialogTitle className="bg-orange-600 text-white">
          Generate Invitation Link
        </DialogTitle>
        <DialogContent dividers className="p-6">
          {selectedEventForInvite && (
            <Typography variant="h6" className="mb-4">
              Event: {selectedEventForInvite.title}
            </Typography>
          )}
          <TextField
            label="Invitee Email (Optional)"
            type="email"
            value={inviteeEmail}
            onChange={(e) => setInviteeEmail(e.target.value)}
            fullWidth
            margin="normal"
            variant="outlined"
            helperText="Leave empty to generate a general link, or enter an email for a targeted invite."
          />
          {generatedInviteLink && (
            <Box className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <Typography variant="body2" className="font-semibold mb-1">
                Generated Link:
              </Typography>
              <TextField
                value={generatedInviteLink}
                fullWidth
                readOnly
                variant="filled"
                size="small"
                InputProps={{
                  readOnly: true,
                }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigator.clipboard.writeText(generatedInviteLink)}
                className="mt-2"
              >
                Copy Link
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions className="p-4 bg-gray-50">
          <Button onClick={handleCloseInviteDialog} color="error" variant="outlined">
            Close
          </Button>
          <Button
            onClick={handleGenerateInviteLink}
            color="primary"
            variant="contained"
            disabled={inviteLinkLoading}
          >
            {inviteLinkLoading ? <CircularProgress size={24} /> : 'Generate Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications in Adventure Map */}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%', borderRadius: '8px' }} elevation={6}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AdventureMap;



// import React, { useState, useEffect } from 'react';
// import { MapIcon } from '@heroicons/react/24/outline';
// import axios from 'axios'; // For making API requests

// const AdventureMap = () => {
//   const [upcomingQuests, setUpcomingQuests] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   // Assuming you have a way to get the user's JWT token
//   // const token = localStorage.getItem('token'); // Or from Redux, Context API

//   useEffect(() => {
//     const fetchQuests = async () => {
//       try {
//         const config = {
//           headers: {
//             // 'Authorization': `Bearer ${token}` // Include JWT for protected routes
//           },
//         };
//         const response = await axios.get('/api/activities/upcoming', config);
//         setUpcomingQuests(response.data.data); // Assuming the response structure is { success: true, data: [...] }
//       } catch (err) {
//         console.error('Failed to fetch upcoming quests:', err);
//         setError('Failed to load upcoming quests.');
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchQuests();
//   }, []);

//   // Your existing calendarDays and month logic
//   const calendarDays = ['28', '29', '30', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '30', '31', '1'];

//   if (loading) return <div className="text-center p-4">Loading quests...</div>;
//   if (error) return <div className="text-center p-4 text-red-500">{error}</div>;

//   return (
//     <div className="bg-white rounded-2xl p-6 shadow-glass border-b-4 border-orange-600 transition-all duration-300 hover:shadow-xl hover:scale-[1.01]">
//       <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 flex items-center">
//         <MapIcon className="w-8 h-8 mr-3 text-orange-600" />
//         Adventure Map
//       </h2>
//       <div className="bg-gray-50 rounded-lg p-4 mb-4 shadow-sm">
//         <div className="flex justify-between items-center mb-3">
//           <button className="text-gray-600 hover:text-gray-900 font-bold text-lg"></button>
//           <span className="font-bold text-xl text-gray-800">May 2025</span> {/* This would need to be dynamic */}
//           <button className="text-gray-600 hover:text-gray-900 font-bold text-lg"></button>
//         </div>
//         <div className="grid grid-cols-7 text-center text-sm font-semibold text-gray-500 mb-2">
//           <span>Sun</span><span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span>
//         </div>
//         <div className="grid grid-cols-7 text-center text-gray-800">
//           {calendarDays.map((day, index) => {
//              // Example: Highlight days with activities
//             const hasActivity = upcomingQuests.some(quest => {
//                 const questDate = new Date(quest.date).getDate(); // Get day number
//                 return String(questDate) === day;
//             });
//             return (
//               <span
//                 key={index}
//                 className={`py-1 ${
//                   day === '2'
//                     ? 'bg-indigo-200 rounded-full text-indigo-800 font-bold'
//                     : day === '22'
//                     ? 'bg-emerald-200 rounded-full text-emerald-800 font-bold border-2 border-emerald-500'
//                     : ['28', '29', '30', '1'].includes(day)
//                     ? 'text-gray-400'
//                     : ''
//                 } ${hasActivity ? 'bg-orange-200 rounded-full text-orange-800 font-bold' : ''}`}
//               >
//                 {day}
//               </span>
//             );
//           })}
//         </div>
//       </div>
//       <h3 className="text-xl font-semibold text-gray-900 mb-3">Upcoming Quests:</h3>
//       <ul className="space-y-3">
//         {upcomingQuests.length > 0 ? (
//           upcomingQuests.map((quest, index) => (
//             <li key={index} className="flex items-center bg-gray-50 p-3 rounded-lg shadow-sm">
//               <span className={`text-sm font-bold ${quest.color} mr-2`}>{quest.date}:</span>
//               <a href={quest.joinLink} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-blue-600 hover:underline">
//                 {quest.task}
//               </a>
//             </li>
//           ))
//         ) : (
//           <p className="text-gray-600">No upcoming quests found.</p>
//         )}
//       </ul>
//     </div>
//   );
// };

// export default AdventureMap;